import { AudioLinesIcon, DownloadIcon } from "lucide-react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AudioAmplitudeVisualizer } from "./-components/AudioAmplitudeVisualizer";
import AnalyzeRange from "./-components/AnalyzeRange";
import SimilarSpeakers from "./-components/SimilarSpeakers";
import type { AudioAnalysisResult, Voice } from "@/lib/types/vexon";
import { similaritySearchQueryOptions } from "@/lib/queries/vexon/similaritySearch";
import { AudioPlayer } from "@/components/ui/audio-player";
import { Button } from "@/components/ui/button";
import verifyUrl from "@/assets/mp3/vexon-cloned.mp3";
import referenceUrl from "@/assets/mp3/vexon-original.mp3";

export const Route = createFileRoute("/_app/vexon/result")({
  component: VexonResult,
});
const vexonResult: AudioAnalysisResult = {
  entry_id: "068e7c65-b6af-79db-8000-673ba9e6f730",
  results: [
    {
      id: "953012e0-7c19-4110-88ec-a9d60f8d25ae",
      chunk_id: "Chunk 1",
      time_range: "00:00 - 00:05",
      start_time: 0,
      end_time: 5,
      identity: {
        result: "Same Person",
        confidence: 1,
        same_person_prob: 0,
        different_person_prob: 1,
      },
      authenticity: {
        result: "Fake Voice",
        confidence: 1,
        real_prob: 0,
        fake_prob: 0,
      },
    },
    {
      id: "f4b1fdc2-b8d6-4aaf-86f7-7e4fca9b995c",
      chunk_id: "Chunk 2",
      time_range: "00:05 - 00:10",
      start_time: 5,
      end_time: 10,
      identity: {
        result: "Same Person",
        confidence: 1,
        same_person_prob: 1,
        different_person_prob: 0,
      },
      authenticity: {
        result: "Fake Voice",
        confidence: 1,
        real_prob: 0,
        fake_prob: 1,
      },
    },
    {
      id: "c9c2212e-7307-411f-8aec-258a0a8131ca",
      chunk_id: "Chunk 3",
      time_range: "00:10 - 00:20",
      start_time: 10,
      end_time: 20,
      identity: {
        result: "Different Person",
        confidence: 1,
        same_person_prob: 0,
        different_person_prob: 1,
      },
      authenticity: {
        result: "Fake Voice",
        confidence: 1,
        real_prob: 0,
        fake_prob: 1,
      },
    },
    {
      id: "b87d0c7d-b495-4a43-9fd0-e70522377ee5",
      chunk_id: "Chunk 4",
      time_range: "00:20 - 00:29",
      start_time: 20,
      end_time: 29,
      identity: {
        result: "Same Person",
        confidence: 1,
        same_person_prob: 1,
        different_person_prob: 0,
      },
      authenticity: {
        result: "Real Voice",
        confidence: 1,
        real_prob: 1,
        fake_prob: 0,
      },
    },
  ],
};

function VexonResult() {
  const { data: similaritySearch } = useQuery(
    similaritySearchQueryOptions(vexonResult.entry_id),
  );

  const [playingVoice, setPlayingVoice] = useState<Voice | null>(null);

  const toggleReferencePlayPause = () => {
    setPlayingVoice((voice) => {
      return voice === "reference" ? null : "reference";
    });
  };

  const toggleVerifyPlayPause = () => {
    setPlayingVoice((voice) => {
      return voice === "verify" ? null : "verify";
    });
  };

  return (
    <div className="w-full rounded-lg border border-gray-200 p-6 dark:border-gray-800">
      <div className="flex flex-col space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Vexon Voice Detector</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Polygraf AI's Voice Verification System
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900">
                <AudioLinesIcon className="h-5 w-5" />
                <p className="text-lg font-medium">Reference Voice</p>
                <Button variant="ghost" size="icon" asChild>
                  <a
                    className="ml-auto justify-self-end"
                    download="vexon-demo-reference.mp3"
                    href={referenceUrl}
                  >
                    <DownloadIcon className="size-1/2" />
                  </a>
                </Button>
              </div>
              <AudioPlayer
                audioUrl={referenceUrl}
                isPlaying={playingVoice === "reference"}
                onPlayPause={toggleReferencePlayPause}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900">
                <AudioLinesIcon className="h-5 w-5" />
                <p className="text-lg font-medium">Voice to Verify</p>
                <Button variant="ghost" size="icon" asChild>
                  <a
                    className="ml-auto justify-self-end"
                    download="vexon-demo-verify.mp3"
                    href={verifyUrl}
                  >
                    <DownloadIcon className="size-1/2" />
                  </a>
                </Button>
              </div>
              <AudioPlayer
                audioUrl={verifyUrl}
                isPlaying={playingVoice === "verify"}
                onPlayPause={toggleVerifyPlayPause}
              />
            </div>
          </div>
          <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />

          <AnalyzeRange data={vexonResult.results} />
          <AudioAmplitudeVisualizer
            fileUrl={verifyUrl}
            data={vexonResult.results}
          />
          {Object.keys(similaritySearch || {}).length > 0 && (
            <SimilarSpeakers data={similaritySearch || {}} />
          )}
        </div>
      </div>
    </div>
  );
}
